import { isEmpty, qidianLog } from '@/utils';
import ActionButton from '@/components/ActionButton';
import { ActionElementType } from '@/components/ActionButton/types';
import { createRoot } from 'react-dom/client';
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback
} from 'react';
import './index.scss';

/**
 * 弹窗底部按钮类型枚举
 * @export
 * @enum {number}
 */
export enum BtnStyleType {
  primary = 'primary',
  default = 'default',
  warn = 'warn'
}

/**
 * 弹窗底部按钮类型与颜色的映射关系
 */
const TypeColorMap: Record<BtnStyleType, string> = {
  [BtnStyleType.primary]: '#3C6EF0',
  [BtnStyleType.default]: '#868D9F',
  [BtnStyleType.warn]: '#FA2C19'
};

/**
 * 底部按钮的props定义
 * @export
 * @interface BtnItem
 */
export interface BtnItem {
  /** 按钮样式类型，决定默认颜色（参见 BtnStyleType）*/
  type: BtnStyleType;
  /** 按钮显示文本 */
  btnText: string;
  /** 行内样式会合并到内部默认样式上，常用于调整尺寸/颜色/字体等 */
  btnStyle?: CSSProperties;
  /** 点击按钮时的回调。回调接收一个`close`函数或类似回调（任何值都可以）*/
  onClick?: (cb: any) => void;
  /** 在通过 `showModal` 显示的场景中，设置为 true 表示点击该按钮后自动关闭弹窗 */
  autoClose?: boolean;
  /** 优先级颜色覆盖：当需要覆盖内置颜色时使用 */
  primaryColor?: string;
  /** 覆盖默认类型颜色 */
  defaultColor?: string;
  /** 覆盖警告类型颜色 */
  warnColor?: string;
  /** 当按钮触发的 onClick 为同步函数并且希望在函数完成后同步关闭弹窗时，将此字段设为 true */
  syncClose?: boolean;
  /** 埋点ID（可选）。传入后，将在按钮点击时自动调用 qidianLog(trackingID, trackingParams)。 */
  trackingID?: string;
  /** 埋点参数（可选）。可以是对象或返回对象的函数；不传则传空对象。 */
  trackingParams?: any;
  /** 允许携带额外任意字段以便扩展（例如 data-xx、id 等） */
  [key: string]: any;
}

/**
 * 弹窗的props定义
 * @export
 * @interface ModalProps
 */
export interface ModalProps {
  /** 弹窗标题，支持 React 节点（字符串/元素等） */
  title?: ReactNode;
  /** 弹窗正文内容，支持 React 节点（字符串/元素等） */
  content?: ReactNode;
  /** 底部按钮组；每项为 `BtnItem`，顺序即为渲染顺序 */
  footer?: BtnItem[];
  /** 控制弹窗可见性；showModal 会强制将其设为 true */
  visible?: boolean;
  /** 关闭弹窗的回调函数。对于由 `showModal` 创建的弹窗，该回调由框架传入以卸载 DOM */
  closeModal?: () => void;
  /** 作为备用的内容传入方式：如果未使用 `content` 可直接通过 children 传入节点 */
  children?: ReactNode;
  /** 自定义外层蒙层类名会被追加到最外层容器 `.modal-pro-mask` 上 */
  maskClassName?: string;
  /** 行内样式覆盖蒙层（`.modal-pro-mask`），类型为 React.CSSProperties */
  maskStyle?: CSSProperties;
  /** 自定义内容容器类名，将会追加到 `.modal-content` 上，便于覆盖圆角/尺寸等样式。*/
  contentClassName?: string;
  /** `.modal-content` 的行内样式覆盖 */
  contentStyle?: CSSProperties;
  /** 直接控制 `.modal-content` 的宽度，支持像素或任意合法 CSS 宽度值 */
  contentWidth?: number | string;
  /** 自定义正文容器类名，将会追加到 `.content-info` 上，便于覆盖默认内边距/对齐方式等。*/
  bodyClassName?: string;
  /** `.content-info` 的行内样式覆盖 */
  bodyStyle?: CSSProperties;
  /** 是否展示右上角的关闭（×）按钮；默认 false */
  showCloseBtn?: boolean;
  /** 点击蒙层（遮罩）是否可以关闭弹窗。默认 true */
  maskClosable?: boolean;
  /** 底部按钮布局方式，默认横向排列 */
  footerLayout?: 'row' | 'column';
}

/**
 * showModal 返回的实例 API
 */
export interface ModalInstance {
  /** 关闭弹窗的函数，调用后会触发卸载并移除 DOM */
  close: () => void;
}

/**
 * 弹窗组件
 * @param {ModalProps} props
 * @return {*}
 */
function Modal(props: ModalProps) {
  // 从 props 中解构出各个字段
  const title = props.title;
  const content = props.content;
  const footer = props.footer;
  const visible = props.visible;
  const closeModal = props.closeModal;
  const children = props.children;
  const maskClassName = props.maskClassName;
  const maskStyle = props.maskStyle;
  const maskClosable =
    typeof props.maskClosable === 'boolean' ? props.maskClosable : false;
  const contentClassName = props.contentClassName;
  const contentStyle = props.contentStyle;
  const contentWidth = props.contentWidth;
  const bodyClassName = props.bodyClassName;
  const bodyStyle = props.bodyStyle;
  const showCloseBtn = props.showCloseBtn;
  const footerLayout = props.footerLayout || 'row';

  // state和ref定义区
  const [modalVisible, setModalVisible] = useState(!!visible); // 弹窗显隐状态
  const historyStateRef = useRef<string | null>(null); // 记录弹窗是否正在展示，用于处理Android返回键/手势

  /**
   * 组件挂载--外部的visible变化时，需要同步modal内部维护的显隐状态
   */
  useEffect(
    function () {
      setModalVisible(!!visible);
    },
    [visible]
  );

  /**
   * 组件挂载--内部维护的显隐状态变化时，需要调用原生的dom方法关闭此父层div
   */
  useEffect(
    function () {
      if (modalVisible) {
        document.body.classList.add('body-overflow-hidden');
      } else {
        document.body.classList.remove('body-overflow-hidden');
      }

      // 页面卸载时把div删除
      return function () {
        document.body.classList.remove('body-overflow-hidden');
      };
    },
    [modalVisible]
  );

  /**
   * 弹窗显示时--处理Android返回键/手势的逻辑
   */
  useEffect(
    function () {
      if (modalVisible) {
        // 当弹窗显示时，向历史记录中添加一个状态
        const modalStateId = 'modal_' + Date.now();
        historyStateRef.current = modalStateId;

        // 当用户点击返回键或使用手势返回时，执行关闭弹窗逻辑
        const handlePopState = function () {
          // 检查当前「弹窗是否展示」的历史记录是否有值
          if (historyStateRef.current) {
            setModalVisible(false);
            // 移除历史记录中的状态
            historyStateRef.current = null;
            // 关闭弹窗（用户执行了返回操作）
            if (closeModal) {
              try {
                closeModal();
              } catch (_) {}
            }
          }
        };

        // 监听popstate事件（用户点击返回键或使用手势返回）
        window.addEventListener('popstate', handlePopState);

        // 清理函数
        return function () {
          window.removeEventListener('popstate', handlePopState);
          historyStateRef.current = null;
        };
      }
    },
    [modalVisible, closeModal]
  );

  /**
   * 关闭当前弹窗
   * 用于处理多个弹窗叠加的情况
   */
  const removeTopmostModal = useCallback(() => {
    setModalVisible(false);
    historyStateRef.current = null;
    // 调用弹窗实例的关闭函数，关闭弹窗
    if (closeModal) {
      try {
        closeModal();
      } catch (_) {}
    }
  }, [closeModal]);

  /**
   * 点击弹窗的关闭按钮
   * 执行关闭逻辑
   */
  const handleCloseBtnClick = useCallback(() => {
    removeTopmostModal();
  }, [removeTopmostModal]);

  /**
   * 格式化footer的onClick函数
   * 内部加上关闭弹窗的逻辑
   */
  const footerOnOkList = useMemo(() => {
    if (!footer || !Array.isArray(footer)) return [] as Array<() => any>;
    return footer.map((item) => {
      return () => {
        // 内置埋点，可传入配置项实现埋点，也可以外部onClick处自己写
        if (item && item.trackingID) {
          try {
            const params =
              typeof item.trackingParams === 'function'
                ? item.trackingParams()
                : item.trackingParams;
            qidianLog(item.trackingID, params || {});
          } catch (_) {}
        }
        // 如果按钮配置了 autoClose，则在点击后同步关闭弹窗
        if (item && item.autoClose) {
          removeTopmostModal();
        }
        // 给这个onClick函数传入一个关闭弹窗的回调函数，供外部调用以关闭弹窗
        if (item && item.onClick) {
          return item.onClick(removeTopmostModal as any) as any;
        }
        return undefined as any;
      };
    });
  }, [footer, removeTopmostModal]);

  const onClickMask = (e: React.MouseEvent<HTMLDivElement>) => {
    // 只在点击到蒙层自身（而非内部内容）时关闭弹窗，避免事件穿透
    if (e.target === e.currentTarget && maskClosable) {
      removeTopmostModal();
    }
  };

  /**
   * 构造弹窗内容的样式
   */
  const mergedContentStyle = useMemo(() => {
    const style: CSSProperties = Object.assign({}, contentStyle);
    if (typeof contentWidth === 'number') {
      style.width = `${contentWidth}px`;
    } else if (typeof contentWidth === 'string' && contentWidth.trim()) {
      style.width = contentWidth;
    } else if (!style.width) {
      style.width = '74.9%';
    }
    return style;
  }, [contentStyle, contentWidth]);

  // 如果弹窗不可见，则不渲染任何内容
  if (!modalVisible) {
    return null;
  }

  /**
   * 渲染模态框底部按钮组
   * @param footer - 按钮组数据
   * @returns - 如果footer不为空，则返回渲染的按钮组；否则返回null
   */
  const renderFooter = function () {
    if (!isEmpty(footer)) {
      return (
        <div
          className={`modal-btn-group ${
            footerLayout === 'column' ? 'column-layout' : ''
          }`.trim()}
        >
          {footer &&
            footer.map(function (item: BtnItem, index: number) {
              // 如果按钮的onClick为同步函数，且syncClose为true，则自动关闭弹窗
              const syncClose =
                typeof item.syncClose === 'boolean' ? item.syncClose : false;

              // 构建按钮的样式
              const colorKey = `${item.type}Color`;
              // 拿到解析后的颜色
              const resolvedColor: string =
                item[colorKey] ||
                TypeColorMap[item.type] ||
                TypeColorMap.primary;
              // 构造默认样式
              const btnStyle: CSSProperties = {
                color: resolvedColor,
                fontSize: '16px',
                fontFamily: 'PingFang SC'
              };
              // 合并外部样式
              if (item.btnStyle) {
                Object.assign(btnStyle, item.btnStyle);
              }
              // 拿出按钮的点击事件，开始渲染按钮
              const onOkHandler = footerOnOkList[index];
              return (
                <ActionButton
                  key={index}
                  as={ActionElementType.a}
                  className={'btn-default'}
                  style={btnStyle}
                  onOk={onOkHandler}
                  clickFinishedCb={syncClose ? removeTopmostModal : undefined}
                >
                  {item.btnText}
                </ActionButton>
              );
            })}
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className={`modal-pro-mask ${maskClassName || ''}`}
      style={maskStyle}
      data-qidian-cid="h5_modal"
      clstag="h5-modal-imp"
      onClick={onClickMask}
    >
      <div
        className={`modal-content ${contentClassName || ''}`}
        style={mergedContentStyle}
      >
        {/* 关闭按钮（占据整行） */}
        {showCloseBtn && (
          <div
            className="modal-close-btn"
            onClick={handleCloseBtnClick}
            role="button"
            aria-label="close"
          >
            ×
          </div>
        )}
        {/* 弹窗的标题 */}
        {title && <div className="modal-content_title">{title}</div>}
        {/* 弹窗的中部内容 */}
        {(content || children) && (
          <div
            className={`content-info ${bodyClassName || ''}`}
            style={bodyStyle}
          >
            {content || children}
          </div>
        )}
        {/* 弹窗的底部按钮 */}
        {renderFooter()}
      </div>
    </div>
  );
}

/**
 * 显示模态框
 * @export
 * @param {ModalProps} props
 */
export function showModal(props: ModalProps) {
  // 创建一个div元素作为弹窗的容器
  const div = document.createElement('div');
  div.className = 'modal-container';
  document.body.appendChild(div);
  const root = createRoot(div);

  // 定义一个卸载函数，用于在组件卸载时移除DOM节点
  const unmountModal = function () {
    try {
      // 先通知react卸载组件
      root.unmount();
      // 接着再将这个div从DOM树中移除
      if (div && div.parentNode) {
        div.parentNode.removeChild(div);
      }
    } catch (_) {}
  };

  // 将卸载函数挂载到div上，以便在组件卸载时调用
  try {
    (div as any).__unmount = unmountModal;
  } catch (_) {}

  // 合并默认样式和传入的props，并打开弹窗的显隐
  const modalProps = Object.assign({}, props, {
    visible: true,
    closeModal: unmountModal
  });

  root.render(<Modal {...(modalProps as ModalProps)} />);

  // 返回实例API（外部可通执行函数得到的实例调用close方法关闭弹窗）
  const instance: ModalInstance = { close: unmountModal };
  return instance;
}

/**
 * 关闭当前最顶层弹窗
 * 用于从嵌套弹窗中主动关闭上层弹窗
 * @export
 * @return {*}
 */
export function closeTopmostModal() {
  try {
    const list = document.querySelectorAll('.modal-container');
    const container =
      list && list.length ? (list[list.length - 1] as any) : null;
    if (container) {
      if (typeof container.__unmount === 'function') {
        container.__unmount();
        return;
      }
      if (container.parentNode) {
        container.parentNode.removeChild(container);
        return;
      }
    }
  } catch (_) {}
}
export default Modal;
